import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { DesktopScreen, MobileTabletScreen, TabletScreen } from '@sm/webassets';
import { Typography, List, ListItem, Row, Col } from '@sm/wds-react';
import { IconChevronDown, IconChevronUp } from '@sm/wds-icons';

import { menuItemPropType } from '../../../propTypes';
import getClickHandlers from '../../../helpers/getClickHandlers';
import './expandable-menu.scss';
import { NAV_VARIANT } from '../../../enums';

export const getLinkTestId = text => `ExpandableMenu__link-${text}`;

const ExpandableMenu = ({
  text,
  href,
  target,
  menuItems,
  isOpen,
  onClick,
  onBlur,
  variant
}) => {
  const getText = () => {
    const textClasses = classnames({
      'sm-expandable-menu__title': true,
      'wds-m-r-2': !href
    });
    if (href) {
      return (
        <a
          href={href}
          className="sm-expandable-menu__base-link"
          target={target}
          {...getClickHandlers(() => {
            if (onClick) onClick();
          })}
          data-testid={getLinkTestId(text)}
        >
          <Typography
            variant="cardTitle"
            className={textClasses}
            color={variant}
          >
            {text}
          </Typography>
        </a>
      );
    }
    return (
      <Typography variant="cardTitle" className={textClasses} color={variant}>
        <div
          role="button"
          tabIndex="0"
          aria-pressed={isOpen}
          {...getClickHandlers(() => {
            if (onClick) onClick();
          })}
          className="sm-expandable-menu__open-text"
        >
          <span className={textClasses}>{text}</span>
          <div className="sm-expandable-menu__chevron">
            <Choose>
              <When condition={isOpen}>
                <IconChevronUp size="xs" />
              </When>
              <Otherwise>
                <IconChevronDown size="xs" />
              </Otherwise>
            </Choose>
          </div>
        </div>
      </Typography>
    );
  };

  return (
    <>
      <DesktopScreen>
        <div
          className="sm-expandable-menu"
          onBlur={
            typeof onBlur === 'function'
              ? // Need to do this to give the click events a chance to resolve
                () => setTimeout(onBlur, 150)
              : undefined
          }
          id={typeof onBlur === 'string' ? onBlur : undefined}
        >
          {getText()}
          <If condition={menuItems.length && isOpen}>
            <div className="sm-expandable-menu__sub-menu">
              <List dense noRules interactive>
                {menuItems.map(item => (
                  <ListItem
                    key={`sm-expandable-menu-item-${item.text}`}
                    isNavigable
                    navigationTarget={item.href}
                    target={item.target}
                    data-testid={getLinkTestId(item.text)}
                  >
                    {item.text}
                  </ListItem>
                ))}
              </List>
            </div>
          </If>
        </div>
      </DesktopScreen>
      <MobileTabletScreen>
        <Row className="sm-expandable-menu__item">
          <TabletScreen>
            <Col xs={3} />
          </TabletScreen>
          <Col>{getText()}</Col>
          <TabletScreen>
            <Col xs={3} />
          </TabletScreen>
        </Row>
        <If condition={menuItems.length && isOpen}>
          <div className="sm-expandable-menu__sub-menu">
            {menuItems.map(item => (
              <a
                href={item.href}
                target={item.target}
                className="sm-expandable-menu__link"
                key={`sm-expandable-menu-item-${item.text}`}
                data-testid={getLinkTestId(item.text)}
              >
                <Row
                  key={`sm-expandable-menu-item-${item.text}`}
                  className="sm-expandable-menu__item"
                >
                  <TabletScreen>
                    <Col xs={3} />
                  </TabletScreen>

                  <Col className="sm-expandable-menu__item-col">
                    <Typography className="sm-expandable-menu__item-text">
                      {item.text}
                    </Typography>
                  </Col>

                  <TabletScreen>
                    <Col xs={3} />
                  </TabletScreen>
                </Row>
              </a>
            ))}
          </div>
        </If>
      </MobileTabletScreen>
    </>
  );
};

ExpandableMenu.propTypes = {
  text: PropTypes.string.isRequired,
  href: PropTypes.string,
  target: PropTypes.string,
  menuItems: PropTypes.arrayOf(menuItemPropType),
  isOpen: PropTypes.bool,
  onClick: PropTypes.func,
  // This can either be a handler or ID to attach to the element
  // the event handler would attach to (for IE 11)
  onBlur: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  variant: PropTypes.oneOf(Object.values(NAV_VARIANT)).isRequired
};

ExpandableMenu.defaultProps = {
  href: null,
  target: '',
  menuItems: [],
  isOpen: false,
  onClick: () => null,
  onBlur: () => null
};

export default ExpandableMenu;
